import { useEffect, useRef, useState } from 'react';
import AboutDialog from '../AboutDialog';
import {
  ALL_PANEL_IDS,
  isPanelOpen,
  PANEL_LABELS,
  type EditorPanelId,
} from '../../types/editorLayout';
import { useEditorLayoutStore } from '../../store/editorLayoutStore';

// ------------------------------------------------------------
// View menu — toggle dock panels and simulation bottom panel
// ------------------------------------------------------------

function CheckIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.25 7.333a1 1 0 0 1-1.414.006l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.543 2.543 6.543-6.61a1 1 0 0 1 1.414-.012Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default function EditorViewMenu() {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const layout = useEditorLayoutStore((s) => s.layout);
  const togglePanel = useEditorLayoutStore((s) => s.togglePanel);
  const setBottomOpen = useEditorLayoutStore((s) => s.setBottomOpen);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggleDockPanel = (panelId: EditorPanelId) => {
    togglePanel(panelId);
  };

  const toggleSimulationPanel = () => {
    setBottomOpen(!layout.bottomOpen);
  };

  return (
    <div ref={rootRef} className="relative hidden sm:block">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="btn-ghost px-3 py-1.5 text-xs"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        View
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 min-w-[13rem] rounded-lg border border-border bg-surface-raised py-1 shadow-elevated"
        >
          <p className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
            Panels
          </p>
          {ALL_PANEL_IDS.map((panelId) => {
            const checked = isPanelOpen(layout, panelId);
            return (
              <button
                key={panelId}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                onClick={() => toggleDockPanel(panelId)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-text hover:bg-surface-overlay"
              >
                <span
                  className={`flex h-3.5 w-3.5 items-center justify-center ${
                    checked ? 'text-accent' : 'text-transparent'
                  }`}
                >
                  <CheckIcon />
                </span>
                <span className="truncate">{PANEL_LABELS[panelId]}</span>
              </button>
            );
          })}
          <div className="my-1 border-t border-border-subtle" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setAboutOpen(true);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-text hover:bg-surface-overlay"
          >
            About
          </button>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={layout.bottomOpen}
            onClick={toggleSimulationPanel}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-text hover:bg-surface-overlay"
          >
            <span
              className={`flex h-3.5 w-3.5 items-center justify-center ${
                layout.bottomOpen ? 'text-accent' : 'text-transparent'
              }`}
            >
              <CheckIcon />
            </span>
            <span className="truncate">Simulation panel</span>
          </button>
        </div>
      )}
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </div>
  );
}
